// src/pages/sms/index.jsx
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import Header from "../../components/ui/Header";
import Sidebar from "../../components/ui/Sidebar";
import Breadcrumbs from "../../components/ui/Breadcrumbs";
import Button from "../../components/ui/Button";
import Icon from "../../components/AppIcon";

import SmsComposeModal from "../../components/sms/SmsComposeModal";
import { listSmsQueue } from "../../lib/sms";

const smsStatusClass = (s) => {
  switch (s) {
    case "sent":
      return "bg-green-50 text-green-700";
    case "failed":
      return "bg-red-50 text-red-700";
    case "sending":
      return "bg-blue-50 text-blue-700";
    default:
      return "bg-muted text-text-secondary";
  }
};

export default function SmsPage() {
  const { t } = useTranslation();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);

  const [queue, setQueue] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueError, setQueueError] = useState("");

  const toggleSidebar = () => setSidebarCollapsed((v) => !v);

  const refreshQueue = async () => {
    setQueueLoading(true);
    setQueueError("");
    try {
      const data = await listSmsQueue({ status: "pending,sending,failed,sent", limit: 200 });
      setQueue(Array.isArray(data?.items) ? data.items : []);
    } catch (e) {
      console.error(e);
      const d = e?.response?.data;
      const msg = typeof d === "string" ? d : (d?.detail || JSON.stringify(d || {}));
      setQueueError(msg || t("sms.queueLoadError"));
    } finally {
      setQueueLoading(false);
    }
  };

  useEffect(() => {
    refreshQueue();
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header isCollapsed={sidebarCollapsed} onToggleSidebar={toggleSidebar} />
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={toggleSidebar} />

      <main
        className={`page-top pb-20 lg:pb-6 transition-all duration-300 ${
          sidebarCollapsed ? "lg:ml-16" : "lg:ml-60"
        }`}
      >
        <div className="p-4 sm:p-6">
          <div className="mb-6">
            <Breadcrumbs items={[{ label: t("sms.title") }]} />

            <div className="mt-4 flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div>
                <h1 className="text-3xl font-bold text-foreground">{t("sms.title")}</h1>
                <p className="text-text-secondary mt-1">
                  {t("sms.subtitle")}
                </p>
              </div>

              <div className="flex">
                <Button
                  variant="default"
                  onClick={() => setComposeOpen(true)}
                  className="flex items-center gap-2 w-full lg:w-auto justify-center"
                >
                  <Icon name="Send" size={18} />
                  <span>{t("sms.compose")}</span>
                </Button>
              </div>
            </div>
          </div>

          <div className="sm:bg-surface sm:border sm:border-border sm:rounded-xl p-0 sm:p-4">
            <div className="flex items-start gap-3">
              <Icon name="Info" size={18} className="text-text-secondary mt-0.5" />
              <div className="text-sm text-text-secondary">
                <div className="font-medium text-foreground">{t("sms.realProbeTitle")}</div>
                <div className="mt-1">
                  {t("sms.realProbeDesc")}
                </div>
                <div className="mt-2">
                  {t("sms.pairingLabel")} <a className="underline" href="/app/business/settings">Business Settings → SMS Gateway</a>
                </div>
              </div>
            </div>
          </div>

          <div className="border-t border-border pt-6 sm:border-t-0 sm:pt-0 sm:bg-surface sm:border sm:border-border sm:rounded-xl p-0 sm:p-4 mt-6">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="font-medium text-foreground">{t("sms.queueTitle")}</div>
                <div className="text-xs text-text-secondary mt-1">
                  {t("sms.queueDesc")}
                </div>
              </div>
              <Button
                variant="outline"
                onClick={refreshQueue}
                className="flex items-center gap-2"
                disabled={queueLoading}
              >
                <Icon name="RefreshCw" size={16} />
                <span>{queueLoading ? t("sms.loading") : t("business.refresh")}</span>
              </Button>
            </div>

            {queueError ? (
              <div className="mt-4 rounded border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {queueError}
              </div>
            ) : null}

            {/* Mobile: listă verticală (stacked) */}
            <div className="mt-6 pt-5 border-t border-border space-y-5 sm:hidden">
              {queue?.length ? (
                queue.map((it) => {
                  const when = it?.scheduled_at ? new Date(it.scheduled_at) : null;
                  const whenTxt = when && !Number.isNaN(when.getTime()) ? when.toLocaleString("ro-RO") : "—";
                  return (
                    <div
                      key={it.id}
                      className="border-b border-border pb-5 last:border-b-0 last:pb-0"
                    >
                      <div className="flex items-center justify-between gap-3 mb-2">
                        <span
                          className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${smsStatusClass(it.status)}`}
                        >
                          {it.status}
                        </span>
                        <span className="text-xs text-text-secondary text-right">{whenTxt}</span>
                      </div>
                      <dl className="text-sm">
                        <div className="flex items-baseline justify-between gap-3 py-1.5 border-b border-dashed border-border">
                          <dt className="text-text-secondary">{t("sms.colPhone")}</dt>
                          <dd className="text-foreground font-medium text-right break-all">{it.to}</dd>
                        </div>
                        <div className="py-1.5">
                          <dt className="text-text-secondary mb-1">{t("sms.colMessage")}</dt>
                          <dd className="text-foreground whitespace-pre-wrap break-words">{it.body}</dd>
                        </div>
                      </dl>
                    </div>
                  );
                })
              ) : (
                <div className="py-3 text-sm text-text-secondary">
                  {queueLoading ? t("sms.loading") : t("sms.nothingInQueue")}
                </div>
              )}
            </div>

            {/* Desktop: tabel */}
            <div className="mt-4 hidden sm:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-text-secondary">
                    <th className="py-2 pr-3">{t("sms.colStatus")}</th>
                    <th className="py-2 pr-3">{t("sms.colWhen")}</th>
                    <th className="py-2 pr-3">{t("sms.colPhone")}</th>
                    <th className="py-2 pr-3">{t("sms.colMessage")}</th>
                  </tr>
                </thead>
                <tbody>
                  {queue?.length ? (
                    queue.map((it) => {
                      const when = it?.scheduled_at ? new Date(it.scheduled_at) : null;
                      const whenTxt = when && !Number.isNaN(when.getTime()) ? when.toLocaleString("ro-RO") : "—";
                      return (
                        <tr key={it.id} className="border-t border-border">
                          <td className="py-2 pr-3 whitespace-nowrap">{it.status}</td>
                          <td className="py-2 pr-3 whitespace-nowrap">{whenTxt}</td>
                          <td className="py-2 pr-3 whitespace-nowrap">{it.to}</td>
                          <td className="py-2 pr-3">{it.body}</td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr className="border-t border-border">
                      <td className="py-3 text-text-secondary" colSpan={4}>
                        {queueLoading ? t("sms.loading") : t("sms.nothingInQueue")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <SmsComposeModal isOpen={composeOpen} onClose={() => { setComposeOpen(false); refreshQueue(); }} />
        </div>
      </main>
    </div>
  );
}
